import { useState, MouseEvent, useEffect, useCallback } from 'react';
import { Form, FormRenderProps } from 'react-final-form';
import { connect, useDispatch } from 'react-redux';
import type { Dispatch } from 'redux';

import { Button } from 'UIPackage/MainModule/components/Button';
import { IAdminKnmFormResponse } from 'APIPackage/AdminModule/interfaces/IAdminKnmFormResponse';
import { TextInputField } from 'UIPackage/MainModule/components/TextInputField';
import { CheckboxField } from 'UIPackage/MainModule/components/CheckboxField';
import { CommonCatalogsNamesEnum } from 'CorePackage/CatalogsModule/enums/CommonCatalogsNamesEnum';
import { RootActions } from 'CorePackage/MainModule/types/RootActions';
import { getCatalogKnmExecutionTypesAll } from 'CorePackage/CatalogsModule/selectors/catalogsSelectors';
import { FieldBlock } from 'UIPackage/FormModule/components/FieldBlock';
import { Row } from 'UIPackage/FormModule/components/Row';
import { ModalActions } from 'UIPackage/ModalModule/components/ModalActions';
import { IRootState } from 'CorePackage/MainModule/interfaces/IRootState';

import { adminCreateCatalogTypes, adminGetCatalogTypes, adminUpdateCatalogTypes } from '../actions/adminAction';
import styles from './AdminEditForm.module.scss';
import { makeRequired } from '../../../CorePackage/MainModule/validators/isRequired';

type KnmFormType = Omit<IAdminKnmFormResponse, 'id'>;

interface IKnmFormEditFormProps {
  onBack: () => void;
}

interface IKnmFormEditFormStoreProps {
  knmForms: IAdminKnmFormResponse[];
}

const KnmFormEditFormWithoutStore = ({ onBack, knmForms }: IKnmFormEditFormProps & IKnmFormEditFormStoreProps) => {
  const dispatch = useDispatch<Dispatch<RootActions>>();
  const [selectedKnmForm, setSelectedKnmForm] = useState<IAdminKnmFormResponse | undefined>();
  const [isNew, setIsNew] = useState(false);
  const [isFormOpen, setIsFormOpen] = useState(false);

  const onRowClick = useCallback(
    (event: MouseEvent<HTMLButtonElement>) => {
      const { id } = event.currentTarget.dataset;
      const knmForm = knmForms.find(k => k.id.toString() === id);

      setSelectedKnmForm(prev => (prev?.id === knmForm?.id ? undefined : knmForm));
    },
    [knmForms],
  );

  useEffect(() => {
    dispatch(adminGetCatalogTypes(CommonCatalogsNamesEnum.KNM_EXECUTION_TYPES_ALL));
  }, [dispatch]);

  const onCreate = useCallback(() => {
    setIsNew(true);
    setSelectedKnmForm({
      id: -1,
      enabled: true,
      code: '',
      name: '',
      weight: 0,
    });
    setIsFormOpen(true);
  }, []);

  const onEdit = useCallback(() => setIsFormOpen(true), []);

  const onCancel = useCallback(() => {
    setIsNew(false);
    setSelectedKnmForm(undefined);
    setIsFormOpen(false);
  }, []);

  const onSubmit = useCallback(
    (values: KnmFormType) => {
      if (isNew) {
        dispatch(
          adminCreateCatalogTypes({ data: values, catalogName: CommonCatalogsNamesEnum.KNM_EXECUTION_TYPES_ALL }),
        );
      } else {
        dispatch(
          adminUpdateCatalogTypes({
            data: { id: selectedKnmForm!.id, ...values },
            catalogName: CommonCatalogsNamesEnum.KNM_EXECUTION_TYPES_ALL,
          }),
        );
      }
      onCancel();
    },
    [dispatch, isNew, onCancel, selectedKnmForm],
  );

  const renderForm = useCallback(
    ({ handleSubmit }: FormRenderProps<KnmFormType>) => (
      <form onSubmit={handleSubmit}>
        <Row>
          <FieldBlock label="Название" color="black">
            <TextInputField name="name" validate={makeRequired('Поле обязательно')} />
          </FieldBlock>
        </Row>
        <Row>
          <FieldBlock label="Код" color="black">
            <TextInputField name="code" disabled={!isNew} />
          </FieldBlock>
        </Row>
        <Row>
          <FieldBlock label="Вес" color="black">
            <TextInputField min={1} max={999999999} type="number" name="weight" />
          </FieldBlock>
        </Row>
        <Row>
          <CheckboxField id="enabled" name="enabled" label="Активен" />
        </Row>
        <ModalActions placement="left" padding="none">
          <Button color="primary" text="Сохранить" id="saveButton" type="submit" variant="contained" />
          <Button color="default" text="Отмена" variant="contained" onClick={onCancel} />
        </ModalActions>
      </form>
    ),
    [isNew, onCancel],
  );

  if (isFormOpen) {
    return (
      <>
        <Form<KnmFormType> onSubmit={onSubmit} initialValues={selectedKnmForm} render={renderForm} />
      </>
    );
  }

  return (
    <div>
      <div className={styles.Table}>
        <span className={styles.Header}>Формы проведения КНМ</span>
        {knmForms &&
          knmForms.map((knmForm: IAdminKnmFormResponse) => (
            <button
              key={knmForm.id}
              className={selectedKnmForm?.id === knmForm.id ? styles.DataRowSelected : styles.DataRow}
              data-id={knmForm.id}
              onClick={onRowClick}
              type="button"
            >
              {knmForm.name}
            </button>
          ))}
      </div>
      <div className={styles.ControlButtons}>
        <Button
          className={styles.Button}
          color="default"
          type="button"
          variant="contained"
          onClick={onBack}
          text="Назад"
        />
        <Button
          className={styles.Button}
          color="primary"
          type="button"
          variant="contained"
          onClick={onCreate}
          text="Создать"
          id="createButton"
        />
        {selectedKnmForm && (
          <Button
            className={styles.Button}
            color="default"
            type="button"
            variant="contained"
            onClick={onEdit}
            text="Редактировать"
          />
        )}
      </div>
    </div>
  );
};

const mapStateToProps = (state: IRootState): IKnmFormEditFormStoreProps => ({
  knmForms: getCatalogKnmExecutionTypesAll(state),
});

export const KnmFormEditForm = connect(mapStateToProps)(KnmFormEditFormWithoutStore);
